/** @jsxImportSource @emotion/react */

import { presentScanNoteVisibilityHint } from '@kaiizen/kai-presenters';
import { getLatestProgressStatus, type WorkStatus } from '@kaiizen/kai-scan-template-system';
import { type SyntheticEvent, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { NOTES_CHARACTER_LIMIT } from '@/Client/noteConstants';
import { CloseIcon, ModalLayout } from '@kaiizen/kai-shiki/react';

import {
  PROGRESS_UPDATE_MODAL_BODY_STYLE,
  PROGRESS_UPDATE_MODAL_CLOSE_STYLE,
  PROGRESS_UPDATE_MODAL_EYEBROW_STYLE,
  PROGRESS_UPDATE_MODAL_FOOTER_STYLE,
  PROGRESS_UPDATE_MODAL_GHOST_BUTTON_STYLE,
  PROGRESS_UPDATE_MODAL_HEAD_STYLE,
  PROGRESS_UPDATE_MODAL_LAYOUT_STYLE,
  PROGRESS_UPDATE_MODAL_NOTE_COUNT_LIMIT_STYLE,
  PROGRESS_UPDATE_MODAL_NOTE_COUNT_STYLE,
  PROGRESS_UPDATE_MODAL_NOTE_HINT_STYLE,
  PROGRESS_UPDATE_MODAL_NOTE_STYLE,
  PROGRESS_UPDATE_MODAL_PANEL_STYLE,
  PROGRESS_UPDATE_MODAL_PRIMARY_BUTTON_STYLE,
  PROGRESS_UPDATE_MODAL_SECTION_LABEL_STYLE,
  PROGRESS_UPDATE_MODAL_SUBTITLE_STYLE,
  PROGRESS_UPDATE_MODAL_TITLE_STYLE,
} from './focusedProgressUpdateModalStyles';
import {
  canSubmitProgressUpdate,
  PROGRESS_UPDATE_NOTE_PLACEHOLDER,
} from './progressUpdateFormLib';
import { ProgressUpdateMediaSection } from './ProgressUpdateMediaSection';
import {
  getProgressUpdateModalSubtitle,
  getProgressUpdateModalTitle,
} from './progressUpdateModalPresentersLib';
import type { ProgressUpdateTarget, StagedProgressPhoto } from './progressUpdateModalTypes';
import { ProgressUpdateStatusGrid } from './ProgressUpdateStatusGrid';
import { useSubmitProgressUpdate } from './useSubmitProgressUpdate';

const FOCUSABLE_ELEMENT_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  'audio[controls]',
  'video[controls]',
  'details > summary:first-of-type',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

const stopPanelEventPropagation = (event: SyntheticEvent) => event.stopPropagation();

const getContainedFocusTarget = (dialog: HTMLElement, shiftKey: boolean) => {
  const focusableElements = [...dialog.querySelectorAll<HTMLElement>('*')].filter(
    element =>
      element.matches(FOCUSABLE_ELEMENT_SELECTOR)
      && element.tabIndex >= 0
      && !element.matches(':disabled')
      && !element.closest('[hidden], [inert], [aria-hidden="true"]'),
  );
  const firstElement = focusableElements[0] ?? dialog;
  const lastElement = focusableElements[focusableElements.length - 1] ?? dialog;
  const activeElement = document.activeElement;

  if (!dialog.contains(activeElement)) return firstElement;
  if (!focusableElements.includes(activeElement as HTMLElement)) {
    return shiftKey ? lastElement : firstElement;
  }
  if (shiftKey && activeElement === firstElement) return lastElement;
  if (!shiftKey && activeElement === lastElement) return firstElement;
};

const containDialogFocus = (event: KeyboardEvent, dialog: HTMLElement) => {
  if (event.key !== 'Tab') return;
  const target = getContainedFocusTarget(dialog, event.shiftKey);
  if (!target) return;
  event.preventDefault();
  target.focus();
};

export const PROGRESS_UPDATE_MODAL_EYEBROW = 'Update progress';
export const PROGRESS_UPDATE_MODAL_SUBMIT_LABEL = 'Update progress';
export const PROGRESS_UPDATE_MODAL_CANCEL_LABEL = 'Cancel';

/** Work-phase composer for posting status, a note, and photos together. */
export const FocusedProgressUpdateModal = ({
  target,
  onClose,
}: {
  target: ProgressUpdateTarget;
  onClose: () => void;
}) => {
  const dialogId = useId();
  const originatingElement = useRef(
    typeof document === 'undefined' || !(document.activeElement instanceof HTMLElement)
      ? undefined
      : document.activeElement,
  );

  const [selectedStatus, setSelectedStatus] = useState<WorkStatus | undefined>();
  const [note, setNote] = useState('');
  const [stagedPhotos, setStagedPhotos] = useState<StagedProgressPhoto[]>([]);
  const { submitProgressUpdate, isSubmitting } = useSubmitProgressUpdate();

  const currentStatus = getLatestProgressStatus(target.task) ?? 'notStarted';

  useEffect(() => {
    setSelectedStatus(undefined);
    setNote('');
    setStagedPhotos([]);
  }, [target.task.id]);

  const canSubmit = useMemo(
    () =>
      canSubmitProgressUpdate({
        selectedStatus,
        currentStatus,
        note,
        photoFiles: stagedPhotos,
      }),
    [selectedStatus, currentStatus, note, stagedPhotos],
  );

  useLayoutEffect(() => {
    const focusReturnTarget = originatingElement.current;
    document.getElementById(dialogId)?.focus();
    return () => {
      if (focusReturnTarget?.isConnected) focusReturnTarget.focus();
    };
  }, [dialogId]);

  useEffect(() => {
    const containFocusOrCloseOnEscape = (event: KeyboardEvent) => {
      const dialog = document.getElementById(dialogId);
      if (dialog) containDialogFocus(event, dialog);
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', containFocusOrCloseOnEscape, true);
    return () => window.removeEventListener('keydown', containFocusOrCloseOnEscape, true);
  }, [dialogId, onClose]);

  const title = getProgressUpdateModalTitle(target);
  const subtitle = getProgressUpdateModalSubtitle(target);

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;
    await submitProgressUpdate({
      task: target.task,
      groupId: target.groupId,
      selectedStatus,
      note,
      stagedPhotos,
      onSuccess: onClose,
    });
  };

  return (
    <ModalLayout
      id={dialogId}
      role='dialog'
      aria-modal='true'
      aria-label={PROGRESS_UPDATE_MODAL_EYEBROW}
      tabIndex={-1}
      css={PROGRESS_UPDATE_MODAL_LAYOUT_STYLE}
      data-testid='focused-progress-update-modal'
      // The modal renders inline inside FocusedScopeEditor's PhotoDropZone (not a
      // portal), so an unstopped drag would bubble past this dialog to that
      // drop zone's handlers - arming its "drop here" overlay underneath even
      // though this modal is visually on top. Its own media section already
      // stops propagation for its dedicated drop target; this catches every
      // other area (status grid, note field, backdrop).
      onDragEnter={stopPanelEventPropagation}
      onDragOver={stopPanelEventPropagation}
      onDragLeave={stopPanelEventPropagation}
      onDrop={stopPanelEventPropagation}
      mediaPane={
        <div
          css={PROGRESS_UPDATE_MODAL_PANEL_STYLE}
          onClick={stopPanelEventPropagation}
          onMouseDown={stopPanelEventPropagation}
        >
          <button
            type='button'
            css={PROGRESS_UPDATE_MODAL_CLOSE_STYLE}
            aria-label='Close'
            onClick={onClose}
          >
            <CloseIcon size={18} />
          </button>
          <div css={PROGRESS_UPDATE_MODAL_HEAD_STYLE}>
            <span css={PROGRESS_UPDATE_MODAL_EYEBROW_STYLE}>{PROGRESS_UPDATE_MODAL_EYEBROW}</span>
            <h2 css={PROGRESS_UPDATE_MODAL_TITLE_STYLE}>{title}</h2>
            {subtitle
              ? <p css={PROGRESS_UPDATE_MODAL_SUBTITLE_STYLE}>{subtitle}</p>
              : null}
          </div>
          <div css={PROGRESS_UPDATE_MODAL_BODY_STYLE}>
            <div css={PROGRESS_UPDATE_MODAL_SECTION_LABEL_STYLE}>Status</div>
            <ProgressUpdateStatusGrid
              selectedStatus={selectedStatus}
              currentStatus={currentStatus}
              onSelectStatus={setSelectedStatus}
            />
            <div css={PROGRESS_UPDATE_MODAL_SECTION_LABEL_STYLE}>Instructions</div>
            <textarea
              css={PROGRESS_UPDATE_MODAL_NOTE_STYLE}
              rows={3}
              maxLength={NOTES_CHARACTER_LIMIT}
              placeholder={PROGRESS_UPDATE_NOTE_PLACEHOLDER}
              value={note}
              onChange={event => setNote(event.target.value)}
            />
            <div css={PROGRESS_UPDATE_MODAL_NOTE_HINT_STYLE}>{presentScanNoteVisibilityHint('external', undefined)}</div>
            <div
              css={[
                PROGRESS_UPDATE_MODAL_NOTE_COUNT_STYLE,
                note.length >= NOTES_CHARACTER_LIMIT && PROGRESS_UPDATE_MODAL_NOTE_COUNT_LIMIT_STYLE,
              ]}
            >
              {note.length} / {NOTES_CHARACTER_LIMIT}
            </div>
            <div css={PROGRESS_UPDATE_MODAL_SECTION_LABEL_STYLE}>Photos</div>
            <ProgressUpdateMediaSection
              stagedPhotos={stagedPhotos}
              onStagedPhotosChange={setStagedPhotos}
            />
          </div>
          <div css={PROGRESS_UPDATE_MODAL_FOOTER_STYLE}>
            <button
              type='button'
              css={PROGRESS_UPDATE_MODAL_GHOST_BUTTON_STYLE}
              onClick={onClose}
            >
              {PROGRESS_UPDATE_MODAL_CANCEL_LABEL}
            </button>
            <button
              type='button'
              css={PROGRESS_UPDATE_MODAL_PRIMARY_BUTTON_STYLE}
              disabled={!canSubmit || isSubmitting}
              onClick={handleSubmit}
            >
              {PROGRESS_UPDATE_MODAL_SUBMIT_LABEL}
            </button>
          </div>
        </div>
      }
      detailPane={null}
      onClick={onClose}
    />
  );
};
